import {
  FromToDateToolbar,
  HaulMethodSegmentedControl,
  RejectsByInsight, RejectsByTimeInsights,
  ResizeableContent,
  RouteBreadcrumbs,
  StatisticInsight,
  StatisticsDataTable,
  ThemedPage,
  useApp
} from "@ots-frontend/ots-ui";
import {
  Card,
  Container,
  Grid,
  Group,
  LoadingOverlay,
  Menu,
  Radio,
  rem,
  ScrollArea,
  Stack,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {statisticsType, toTransportMethod} from "@ots-frontend/ots-data";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useDispatch} from "react-redux";
import {getInsightsFilter, getInsightsHaulMethod} from "../../store/insights/selectors";
import {useGetInsights} from "../../components/hooks/useGetInsights";
import {setInsightsFilter, setInsightsHaulMethod} from "../../store/insights/actions";

export const RejectsBySearchPage = () => {

  const dispatch = useDispatch()
  const {t} = useTranslation(["translation", "common"]);
  const {regionId, appCategory} = useApp()

  const insightsFilter = useAppSelector(getInsightsFilter)
  const selectedHaulMethod = useAppSelector(getInsightsHaulMethod)

  const [grouping, setGrouping] = useState<string>("route")
  const [chartType, setChartType] = useState<"pie" | "line" | "bar">("pie")

  const insights = useGetInsights({
    fromDttm: insightsFilter.fromDttm,
    toDttm: insightsFilter.toDttm,
    regionId: regionId,
    category: appCategory,
  })

  const {
    statistics: {
      data: statistics,
      isLoading: isStatisticsLoading,
      refetch: refetchStatistics
    }
  } = insights;

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    dispatch(setInsightsFilter({
      fromDttm: fromDate.getTime(),
      toDttm: toDate?.getTime()
    }));
  }, [dispatch]);

  const onHaulMethodChange = useCallback((value: string) => {
    dispatch(setInsightsHaulMethod(value === 'all' ? undefined : toTransportMethod(value)))
  }, [dispatch])

  const data = useMemo(() => {
    if (grouping === "truckCompany") {
      return statistics?.truckCompanyStats
    } else if (grouping === "routeGroup") {
      return statistics?.pickupRouteGroupStats
    } else if (grouping === "route") {
      return statistics?.pickupRouteStats
    } else if (grouping === "site") {
      return statistics?.pickupSiteStats
    } else {
      return statistics?.regionStats
    }
  }, [
    grouping,
    statistics?.truckCompanyStats,
    statistics?.pickupRouteGroupStats,
    statistics?.pickupRouteStats,
    statistics?.pickupSiteStats,
    statistics?.regionStats
  ])

  const onChartTypeChange = useCallback((value : string) => {
    if (value === "line" || value === "bar") {
      setChartType(value)
    } else {
      setChartType("pie")
    }
  }, [])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <ScrollArea h={size.height} p="sm" offsetScrollbars={true}>
                <RouteBreadcrumbs p="sm"/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.rejectsby.title", "Insights: Rejects By", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("commons.action.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetchStatistics()}>
                          {t("commons.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <FromToDateToolbar
                    defaultValues={{
                      fromDate: new Date(insightsFilter.fromDttm),
                      toDate: insightsFilter.toDttm ? new Date(insightsFilter.toDttm) : undefined,
                    }}
                    loading={isStatisticsLoading}
                    onSubmit={onFilterChange}/>
                  <Group pt="md" position="center">
                    <HaulMethodSegmentedControl
                      defaultValue={selectedHaulMethod ? selectedHaulMethod.code : 'all'}
                      onChange={onHaulMethodChange}
                    />
                  </Group>
                </Card>

                <LoadingOverlay visible={isStatisticsLoading ?? false}/>

                <Grid>
                  <Grid.Col span={10}>
                    {chartType === "pie" &&
                      <RejectsByInsight
                        id={"search_rejects_by_chart"}
                        category={appCategory}
                        method={selectedHaulMethod}
                        data={data}
                        tooltipPos={'left'}
                        containerProps={{
                          h: 400
                        }}/>
                    }
                    {chartType !== "pie" &&
                      <RejectsByTimeInsights
                        id={"search_rejects_by_time_chart"}
                        fromDttm={insightsFilter.fromDttm}
                        toDttm={insightsFilter.toDttm}
                        category={appCategory}
                        data={data}
                        type={chartType}
                        stacked={{
                          enabled: true,
                          filled: true
                        }}
                        tooltipPos={'left'}
                        containerProps={{
                          h: 400
                        }}/>
                    }
                  </Grid.Col>
                  <Grid.Col span={2}>
                    <Stack>
                      <StatisticInsight
                        title={"Total"}
                        statistics={data}
                        type={statisticsType.rejects}
                        method={selectedHaulMethod}
                        category={appCategory}
                        loading={isStatisticsLoading}/>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Radio.Group
                          name="chartType"
                          value={chartType}
                          onChange={onChartTypeChange}>
                          <Stack mt="xs">
                            <Radio value="pie" label={"Pie"}/>
                            <Radio value="line" label={"Line"}/>
                            <Radio value="bar" label={"Bar"}/>
                          </Stack>
                        </Radio.Group>
                      </Card>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Radio.Group
                          name="grouping"
                          value={grouping}
                          onChange={setGrouping}>
                          <Stack mt="xs">
                            <Radio value="truckCompany" label={t("common.insights.grouping.truckcompany", "Hauler")}/>
                            <Radio value="routeGroup" label={t("common.insights.grouping.routegroup", "Route Group")}/>
                            <Radio value="route" label={t("common.insights.grouping.route", "Route")}/>
                            <Radio value="site" label={t("common.insights.grouping.truckcompany", "Site")}/>
                          </Stack>
                        </Radio.Group>
                      </Card>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Container fluid={true} h={600}>
                      <StatisticsDataTable
                        m="md"
                        withBorder
                        method={selectedHaulMethod}
                        types={[statisticsType.rejects]}
                        category={appCategory}
                        records={data}
                        recordsPerPageOptions={[10]}
                        fetching={isStatisticsLoading}
                        hideExpansion={true}
                        showTotals={true}/>
                    </Container>
                  </Grid.Col>
                </Grid>
              </ScrollArea>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
